import React from 'react';
import {
  ShieldAlert, FileSearch, CopyCheck, AlertTriangle, AlignJustify, ClipboardCheck, FileMinus,
} from 'lucide-react';
import { Card } from './ui/card';

/**
 * IntegrityCapability — "Operator Integralności" capability strip.
 *
 * Embedded inside Compliance/Gov pages as a capability layer (not a separate
 * public route). Communicates HussarAi's discrepancy / missing-info / duplicate
 * detection in a way that NEVER accuses, classifies, or decides — only
 * surfaces signals for a human reviewer.
 *
 * Brand rules enforced inline:
 *   • core line: "AI wskazuje sygnały. Człowiek prowadzi ocenę."
 *   • doctrine: "HussarAi ocenia spójność sprawy, nie wartość człowieka."
 *   • framing: "To są sygnały do weryfikacji, nie stwierdzenie nadużycia."
 */
const cards = [
  { icon: FileSearch,      title: 'Niespójności w dokumentach',
    body: 'Różne daty, nazwy, numery, adresy lub informacje występujące w tej samej sprawie.' },
  { icon: FileMinus,       title: 'Braki formalne',
    body: 'Brakujące załączniki, podpisy, daty ważności lub wymagane informacje.' },
  { icon: CopyCheck,       title: 'Duplikaty',
    body: 'Powtarzające się zgłoszenia, dane kontaktowe, dokumenty lub wzorce spraw.' },
  { icon: AlertTriangle,   title: 'Nietypowe wzorce',
    body: 'Sygnały procesowe wymagające dodatkowej kontroli przez człowieka.' },
  { icon: AlignJustify,    title: 'Sprzeczności w opisie',
    body: 'Rozbieżności między opisem sprawy, dokumentami i deklarowanymi terminami.' },
  { icon: ClipboardCheck,  title: 'Lista do kontroli',
    body: 'Uporządkowana checklista punktów wymagających weryfikacji.' },
];

const IntegrityCapability = ({ testIdPrefix = 'integrity' }) => (
  <section className="hussar-pol-op-section" data-testid={`${testIdPrefix}-section`}>
    <h2 className="hussar-agenci-h2">
      <ShieldAlert className="w-6 h-6 inline-block mr-2" />
      Wykrywanie niespójności i sygnałów do weryfikacji
    </h2>
    <p className="hussar-pol-op-body" data-testid={`${testIdPrefix}-body`}>
      HussarAi może wspierać zespoły w wykrywaniu rozbieżności między dokumentami, opisem sprawy,
      datami, terminami i wcześniejszymi informacjami. System tworzy listę sygnałów do sprawdzenia,
      ale nie przesądza o nadużyciu ani nie wydaje decyzji.
    </p>
    <p className="hussar-pol-op-body" style={{ fontWeight: 600, marginTop: '8px' }}
       data-testid={`${testIdPrefix}-coreline`}>
      „AI wskazuje sygnały. Człowiek prowadzi ocenę.”
    </p>
    <p className="hussar-tc-meta" style={{ marginTop: '4px' }} data-testid={`${testIdPrefix}-doctrine`}>
      „HussarAi ocenia spójność sprawy, nie wartość człowieka.”
    </p>
    <div className="hussar-agenci-deliverables-grid" style={{ marginTop: '20px' }}>
      {cards.map((c, i) => (
        <Card key={c.title} className="hussar-agenci-deliverable-card"
              data-testid={`${testIdPrefix}-card-${i}`}>
          <div className="hussar-agenci-deliverable-icon"><c.icon className="w-5 h-5" /></div>
          <h3 className="hussar-agenci-deliverable-title" style={{ fontSize: '15px' }}>{c.title}</h3>
          <p className="hussar-agenci-deliverable-body">{c.body}</p>
        </Card>
      ))}
    </div>
    <p className="hussar-tc-meta" style={{ marginTop: '16px' }} data-testid={`${testIdPrefix}-framing`}>
      To są sygnały do weryfikacji, nie stwierdzenie nadużycia.
    </p>
    <p className="hussar-tc-meta" data-testid={`${testIdPrefix}-disclaimer`}>
      Wykryte sygnały mają charakter pomocniczy i organizacyjny. Nie stanowią stwierdzenia
      nadużycia, winy ani decyzji prawnej. Wynik wymaga weryfikacji przez człowieka.
    </p>
  </section>
);

export default IntegrityCapability;
